import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Moveable from 'react-moveable';
import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import ItensTableEditavel from '../ItensTableEditavel';
import CanvasElement from './CanvasElement';
import ElementToolbar, { SelecaoToolbar } from './ElementToolbar';
import ElementsSidebar from './ElementsSidebar';
import LayersSidebar from './LayersSidebar';
import { DOCUMENTO_DESIGN as D } from '../../EditorDocumento.design';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from 'src/@/components/ui/context-menu';
import {
  PAGE_W, PAGE_H, normalizarCanvas, novoElemento, novaPagina, novoElementoFormaPagamento,
  novoGrupoId, montarArvoreCamadas, achatarArvoreCamadas,
} from './canvasUtils';

const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5];

// Editor de design livre (estilo Canva): páginas A4 com elementos posicionados
// livremente. Espelha o contrato do DocumentCanvas: recebe `proposta` e `onChange(patch)`.
// `ocultarPaleta`: esconde a coluna de elementos (usado no modo conversa com o agente,
// quando a sidebar do chat alarga e o canvas precisa do espaço).
export default function CanvasEditor({ proposta, onChange, ocultarPaleta }) {
  const canvas = useMemo(() => normalizarCanvas(proposta.canvas), [proposta.canvas]);

  // Seleção do canvas: sempre uma página só, com um ou vários elementos.
  const [selecao, setSelecao] = useState(null); // { pageIndex, elIds: [] }
  const [editingId, setEditingId] = useState(null);
  const [paginaAtiva, setPaginaAtiva] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [itensDialogOpen, setItensDialogOpen] = useState(false);
  const [contatoDialogOpen, setContatoDialogOpen] = useState(false);

  const elementRefs = useRef({});
  const pageRefs = useRef([]);
  const scrollRef = useRef(null);
  const undoStack = useRef([]);
  const dragState = useRef(null); // valores em curso durante drag/resize/rotate
  const dragOrigin = useRef(null); // posição/tamanho do elemento no início do drag/resize
  const moveableRef = useRef(null);
  const ancoraRef = useRef(null); // última camada clicada — base do Shift+clique no painel

  const registerRef = useCallback((elId, node) => {
    if (node) elementRefs.current[elId] = node;
    else delete elementRefs.current[elId];
  }, []);

  const commitCanvas = useCallback((novoCanvas, { pushUndo = true } = {}) => {
    if (pushUndo) {
      undoStack.current.push(JSON.parse(JSON.stringify(canvas)));
      if (undoStack.current.length > 50) undoStack.current.shift();
    }
    onChange({ canvas: novoCanvas });
  }, [canvas, onChange]);

  const getElemento = useCallback((sel) => {
    if (!sel) return null;
    return canvas.paginas[sel.pageIndex]?.elementos?.find(e => e.el_id === sel.elId) || null;
  }, [canvas]);

  const selectedIds = useMemo(() => selecao?.elIds || [], [selecao]);
  // Boa parte das ações só faz sentido com UM elemento — `selected` mantém o contrato
  // antigo ({ pageIndex, elId }) e fica nulo quando há seleção múltipla.
  const selected = selecao && selectedIds.length === 1 ? { pageIndex: selecao.pageIndex, elId: selectedIds[0] } : null;
  const multiSelecao = selectedIds.length > 1;
  const elementoSelecionado = getElemento(selected);
  const elementosSelecionados = useMemo(
    () => (canvas.paginas[selecao?.pageIndex]?.elementos || []).filter(e => selectedIds.includes(e.el_id)),
    [canvas, selecao, selectedIds],
  );
  const gruposSelecionados = useMemo(
    () => [...new Set(elementosSelecionados.map(e => e.grupo_id).filter(Boolean))],
    [elementosSelecionados],
  );

  const limparSelecao = useCallback(() => { setSelecao(null); ancoraRef.current = null; }, []);

  const selecionarIds = useCallback((pageIndex, elIds) => {
    setSelecao(elIds.length ? { pageIndex, elIds } : null);
  }, []);

  // Ids que devem entrar/sair juntos ao clicar num elemento: um membro de grupo
  // arrasta o grupo inteiro (comportamento padrão de editor de design).
  const idsDoAlvo = useCallback((pageIndex, elId) => {
    const els = canvas.paginas[pageIndex]?.elementos || [];
    const alvo = els.find(e => e.el_id === elId);
    if (!alvo) return [];
    if (!alvo.grupo_id) return [elId];
    return els.filter(e => e.grupo_id === alvo.grupo_id).map(e => e.el_id);
  }, [canvas]);

  // Clique numa camada (documento ou painel). `toggle` = Ctrl/Cmd (soma/remove),
  // `intervalo` = Shift (do último clicado até este, na ordem do painel).
  const selecionarElemento = useCallback((pageIndex, elId, { toggle = false, intervalo = false } = {}) => {
    const pagina = canvas.paginas[pageIndex];
    if (!pagina) return;
    const alvoIds = idsDoAlvo(pageIndex, elId);
    if (!alvoIds.length) return;

    const mesmaPagina = selecao?.pageIndex === pageIndex;
    if (intervalo && mesmaPagina && ancoraRef.current) {
      const ordem = achatarArvoreCamadas(montarArvoreCamadas(pagina.elementos, pagina.grupos));
      const de = ordem.indexOf(ancoraRef.current);
      const ate = ordem.indexOf(elId);
      if (de >= 0 && ate >= 0) {
        const faixa = ordem.slice(Math.min(de, ate), Math.max(de, ate) + 1);
        // Puxa junto o grupo inteiro de quem estiver parcialmente na faixa
        const completo = [...new Set(faixa.flatMap(id => idsDoAlvo(pageIndex, id)))];
        selecionarIds(pageIndex, completo);
        return;
      }
    }
    if (toggle && mesmaPagina) {
      const atuais = new Set(selectedIds);
      const jaTodos = alvoIds.every(id => atuais.has(id));
      alvoIds.forEach(id => (jaTodos ? atuais.delete(id) : atuais.add(id)));
      ancoraRef.current = elId;
      selecionarIds(pageIndex, [...atuais]);
      return;
    }
    ancoraRef.current = elId;
    selecionarIds(pageIndex, alvoIds);
  }, [canvas, idsDoAlvo, selecao, selectedIds, selecionarIds]);

  const patchElemento = useCallback((sel, patch, opts) => {
    if (!sel) return;
    commitCanvas({
      ...canvas,
      paginas: canvas.paginas.map((p, i) => i !== sel.pageIndex ? p : {
        ...p,
        elementos: p.elementos.map(e => e.el_id === sel.elId ? { ...e, ...patch } : e),
      }),
    }, opts);
  }, [canvas, commitCanvas]);

  // Aplica um patch (objeto ou função do elemento) em vários elementos da mesma página.
  const patchElementos = useCallback((pageIndex, elIds, patch, opts) => {
    const ids = new Set(elIds);
    if (!ids.size) return;
    commitCanvas({
      ...canvas,
      paginas: canvas.paginas.map((p, i) => i !== pageIndex ? p : {
        ...p,
        elementos: p.elementos.map(e => ids.has(e.el_id)
          ? { ...e, ...(typeof patch === 'function' ? patch(e) : patch) }
          : e),
      }),
    }, opts);
  }, [canvas, commitCanvas]);

  // Reatribui z denso seguindo a ordem da árvore de camadas (frente → fundo),
  // garantindo que os membros de um grupo fiquem sempre contíguos na pilha.
  const comZDaArvore = (elementos, grupos) => {
    const ordem = achatarArvoreCamadas(montarArvoreCamadas(elementos, grupos));
    const zPorId = new Map(ordem.map((id, i) => [id, ordem.length - 1 - i]));
    return elementos.map(e => zPorId.has(e.el_id) ? { ...e, z: zPorId.get(e.el_id) } : e);
  };

  // Descarta metadados de grupos que ficaram sem membros (após remover/desagrupar).
  const gruposEmUso = (elementos, grupos) => {
    const usados = new Set(elementos.map(e => e.grupo_id).filter(Boolean));
    return (grupos || []).filter(g => usados.has(g.grupo_id));
  };

  const maxZ = (pageIndex) => Math.max(0, ...(canvas.paginas[pageIndex]?.elementos || []).map(e => e.z || 0));

  // Insere um elemento numa página específica. Não depende do estado `paginaAtiva`
  // (que é assíncrono) — necessário para o drop funcionar corretamente quando o
  // usuário solta numa página diferente da atualmente ativa.
  const addElementoEmPagina = (tipo, pageIndex, overrides = {}) => {
    // "Forma de pagamento" não é um tipo dedicado — insere um texto pré-preenchido.
    const el = tipo === 'forma_pagamento'
      ? novoElementoFormaPagamento({ z: maxZ(pageIndex) + 1, ...overrides })
      : novoElemento(tipo, { z: maxZ(pageIndex) + 1, ...overrides });
    commitCanvas({
      ...canvas,
      paginas: canvas.paginas.map((p, i) => i !== pageIndex ? p : { ...p, elementos: [...p.elementos, el] }),
    });
    setPaginaAtiva(pageIndex);
    selecionarIds(pageIndex, [el.el_id]);
  };

  const addElemento = (tipo, overrides = {}) => {
    addElementoEmPagina(tipo, Math.min(paginaAtiva, canvas.paginas.length - 1), overrides);
  };

  // Drop de um item arrastado da paleta lateral: posiciona o elemento exatamente
  // onde o usuário soltou (centralizado no cursor), convertendo coordenadas de tela
  // para o espaço da página (794×1123) — a página é escalada visualmente pelo zoom,
  // então getBoundingClientRect() já reflete o tamanho pós-zoom.
  const handleDropElemento = (e, pageIndex) => {
    e.preventDefault();
    const tipo = e.dataTransfer.getData('application/x-proposta-elemento');
    if (!tipo) return;
    const pageEl = pageRefs.current[pageIndex];
    if (!pageEl) return;
    const rect = pageEl.getBoundingClientRect();
    const base = tipo === 'forma_pagamento' ? novoElementoFormaPagamento() : novoElemento(tipo);
    const dropX = (e.clientX - rect.left) / zoom;
    const dropY = (e.clientY - rect.top) / zoom;
    const x = Math.round(Math.min(Math.max(0, dropX - base.largura / 2), Math.max(0, PAGE_W - base.largura)));
    const y = Math.round(Math.min(Math.max(0, dropY - base.altura / 2), Math.max(0, PAGE_H - base.altura)));
    addElementoEmPagina(tipo, pageIndex, { x, y });
  };

  const removerElementos = (pageIndex, elIds) => {
    const ids = new Set(elIds);
    if (!ids.size) return;
    commitCanvas({
      ...canvas,
      paginas: canvas.paginas.map((p, i) => {
        if (i !== pageIndex) return p;
        const elementos = p.elementos.filter(e => !ids.has(e.el_id));
        return { ...p, elementos, grupos: gruposEmUso(elementos, p.grupos) };
      }),
    });
    limparSelecao();
    setEditingId(null);
  };

  const removeElemento = (sel) => {
    if (sel) removerElementos(sel.pageIndex, [sel.elId]);
  };

  // Duplica os elementos informados preservando o agrupamento — a cópia de um grupo
  // vira um grupo novo, não membros do original.
  const duplicarElementos = (pageIndex, elIds) => {
    const ids = new Set(elIds);
    const originais = (canvas.paginas[pageIndex]?.elementos || []).filter(e => ids.has(e.el_id));
    if (!originais.length) return;
    const novosGrupos = new Map();
    let z = maxZ(pageIndex);
    const copias = originais.map(el => {
      const copia = { ...JSON.parse(JSON.stringify(el)), el_id: novoElemento(el.tipo).el_id, x: el.x + 16, y: el.y + 16, z: ++z };
      if (el.grupo_id) {
        if (!novosGrupos.has(el.grupo_id)) novosGrupos.set(el.grupo_id, novoGrupoId());
        copia.grupo_id = novosGrupos.get(el.grupo_id);
      }
      return copia;
    });
    commitCanvas({
      ...canvas,
      paginas: canvas.paginas.map((p, i) => i !== pageIndex ? p : {
        ...p,
        elementos: [...p.elementos, ...copias],
        grupos: [
          ...(p.grupos || []),
          ...[...novosGrupos.entries()].map(([antigo, novo]) => ({
            grupo_id: novo,
            nome: (p.grupos || []).find(g => g.grupo_id === antigo)?.nome || 'Grupo',
          })),
        ],
      }),
    });
    selecionarIds(pageIndex, copias.map(c => c.el_id));
  };

  const duplicarElemento = (sel) => {
    if (sel) duplicarElementos(sel.pageIndex, [sel.elId]);
  };

  // Tranca/destranca camadas: elemento trancado não é selecionável nem arrastável
  // no documento (só pelo painel de camadas, para destrancar). `valor` omitido
  // inverte o estado atual do primeiro elemento.
  const toggleBloqueio = (elIds, valor, pageIndex = selecao?.pageIndex ?? paginaAtiva) => {
    const ids = Array.isArray(elIds) ? elIds : [elIds];
    const els = (canvas.paginas[pageIndex]?.elementos || []).filter(e => ids.includes(e.el_id));
    if (!els.length) return;
    const alvo = valor !== undefined ? valor : !els[0].bloqueado;
    patchElementos(pageIndex, ids, { bloqueado: alvo });
  };

  // ── Grupos ──
  // Agrupar = marcar os elementos com o mesmo grupo_id e normalizar o z para que
  // fiquem contíguos na pilha (o painel os mostra aninhados e eles passam a ser
  // selecionados/movidos juntos).
  const agrupar = (elIds = selectedIds, pageIndex = selecao?.pageIndex ?? paginaAtiva) => {
    const ids = new Set(elIds);
    if (ids.size < 2) return;
    const gid = novoGrupoId();
    commitCanvas({
      ...canvas,
      paginas: canvas.paginas.map((p, i) => {
        if (i !== pageIndex) return p;
        const elementos = p.elementos.map(e => ids.has(e.el_id) ? { ...e, grupo_id: gid } : e);
        const grupos = [
          ...gruposEmUso(elementos, p.grupos),
          { grupo_id: gid, nome: `Grupo ${(gruposEmUso(elementos, p.grupos).length || 0) + 1}` },
        ];
        return { ...p, elementos: comZDaArvore(elementos, grupos), grupos };
      }),
    });
    selecionarIds(pageIndex, [...ids]);
  };

  const desagrupar = (grupoIds = gruposSelecionados, pageIndex = selecao?.pageIndex ?? paginaAtiva) => {
    const gids = new Set(grupoIds);
    if (!gids.size) return;
    commitCanvas({
      ...canvas,
      paginas: canvas.paginas.map((p, i) => {
        if (i !== pageIndex) return p;
        const elementos = p.elementos.map(e => gids.has(e.grupo_id) ? { ...e, grupo_id: undefined } : e);
        return { ...p, elementos, grupos: gruposEmUso(elementos, p.grupos) };
      }),
    });
  };

  const renomearGrupo = (grupoId, nome, pageIndex = paginaAtiva) => {
    commitCanvas({
      ...canvas,
      paginas: canvas.paginas.map((p, i) => i !== pageIndex ? p : {
        ...p,
        grupos: (p.grupos || []).map(g => g.grupo_id === grupoId ? { ...g, nome } : g),
      }),
    });
  };

  // Manda a seleção inteira para a frente/para trás da pilha, preservando a ordem
  // relativa entre os selecionados (importante para grupos). Reordena pela árvore em
  // vez de mexer no z solto — assim o z nunca fica negativo (z-index negativo cairia
  // atrás do próprio fundo da página) nem quebra a contiguidade dos grupos.
  const moverZ = (sel, dir) => {
    const pageIndex = sel?.pageIndex ?? selecao?.pageIndex ?? paginaAtiva;
    const ids = sel ? [sel.elId] : selectedIds;
    const pagina = canvas.paginas[pageIndex];
    if (!ids.length || !pagina) return;
    const ordem = achatarArvoreCamadas(montarArvoreCamadas(pagina.elementos, pagina.grupos));
    const movidos = ordem.filter(id => ids.includes(id));
    const resto = ordem.filter(id => !ids.includes(id));
    reordenarCamadas(dir > 0 ? [...movidos, ...resto] : [...resto, ...movidos], pageIndex);
  };

  // Descobre qual elemento está sob o cursor num clique direito na página (o clique
  // chega no wrapper da página, não no elemento): converte a posição do mouse para
  // coordenadas da página (compensando o zoom) e pega o de maior z que contém o ponto.
  const hitTestElemento = (e, pageIndex) => {
    const rect = pageRefs.current[pageIndex]?.getBoundingClientRect();
    if (!rect) return null;
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;
    const els = canvas.paginas[pageIndex]?.elementos || [];
    return els
      .filter(el => !el.bloqueado && x >= el.x && x <= el.x + el.largura && y >= el.y && y <= el.y + el.altura)
      .sort((a, b) => (b.z || 0) - (a.z || 0))[0] || null;
  };

  // Reordena as camadas da página a partir da nova ordem FRENTE→FUNDO (como o painel
  // de camadas exibe) e reatribui z de forma densa (frente = maior z). Robusto a z
  // duplicado/ausente — usado tanto pelas setas quanto pelo arrastar-e-soltar.
  const reordenarCamadas = (frontToBackIds, pageIndex = paginaAtiva) => {
    const total = frontToBackIds.length;
    const zPorId = new Map(frontToBackIds.map((id, i) => [id, total - 1 - i]));
    commitCanvas({
      ...canvas,
      paginas: canvas.paginas.map((p, i) => i !== pageIndex ? p : {
        ...p,
        elementos: p.elementos.map(e => zPorId.has(e.el_id) ? { ...e, z: zPorId.get(e.el_id) } : e),
      }),
    });
  };

  // Move uma camada UM passo na pilha (setas do painel / menu). dir>0 = para frente
  // (sobe no painel, onde a frente fica no topo). Membro de grupo anda só entre os
  // irmãos; elemento solto anda entre os nós de topo (pulando o grupo inteiro).
  const moverCamadaStep = (elId, dir, pageIndex = paginaAtiva) => {
    const pagina = canvas.paginas[pageIndex];
    if (!pagina) return;
    const arvore = montarArvoreCamadas(pagina.elementos, pagina.grupos);
    const el = pagina.elementos.find(e => e.el_id === elId);

    if (el?.grupo_id) {
      const no = arvore.find(n => n.tipo === 'grupo' && n.grupo_id === el.grupo_id);
      if (!no) return;
      const filhos = [...no.filhos];
      const idx = filhos.findIndex(f => f.el_id === elId);
      const alvo = idx - dir;
      if (idx < 0 || alvo < 0 || alvo >= filhos.length) return;
      [filhos[idx], filhos[alvo]] = [filhos[alvo], filhos[idx]];
      reordenarCamadas(achatarArvoreCamadas(arvore.map(n => n === no ? { ...n, filhos } : n)), pageIndex);
      return;
    }

    const nos = [...arvore];
    const idx = nos.findIndex(n => n.tipo === 'elemento' && n.el.el_id === elId);
    const alvo = idx - dir;
    if (idx < 0 || alvo < 0 || alvo >= nos.length) return;
    [nos[idx], nos[alvo]] = [nos[alvo], nos[idx]];
    reordenarCamadas(achatarArvoreCamadas(nos), pageIndex);
  };

  const addPagina = () => {
    commitCanvas({ ...canvas, paginas: [...canvas.paginas, novaPagina()] });
    setPaginaAtiva(canvas.paginas.length);
  };

  const removePagina = (index) => {
    if (canvas.paginas.length <= 1) return;
    if (canvas.paginas[index].elementos.length > 0 && !window.confirm('Remover esta página e todos os seus elementos?')) return;
    commitCanvas({ ...canvas, paginas: canvas.paginas.filter((_, i) => i !== index) });
    limparSelecao();
    setPaginaAtiva(p => Math.max(0, Math.min(p, canvas.paginas.length - 2)));
  };

  const undo = useCallback(() => {
    const anterior = undoStack.current.pop();
    if (!anterior) return;
    limparSelecao();
    setEditingId(null);
    onChange({ canvas: anterior });
  }, [onChange, limparSelecao]);

  // Atalhos de teclado
  useEffect(() => {
    const handler = (e) => {
      const tag = e.target?.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || e.target?.isContentEditable) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undo();
        return;
      }
      if (!selecao || !selectedIds.length) return;

      if (e.key === 'Escape') {
        limparSelecao();
        return;
      }
      // Ctrl+G agrupa / Ctrl+Shift+G desagrupa (padrão de editores de design)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        if (e.shiftKey) desagrupar();
        else agrupar();
        return;
      }
      // Camada trancada: ignora mover/duplicar/excluir pelo teclado
      const editaveis = elementosSelecionados.filter(el => !el.bloqueado);
      if (!editaveis.length) return;
      const ids = editaveis.map(el => el.el_id);

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        duplicarElementos(selecao.pageIndex, ids);
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        removerElementos(selecao.pageIndex, ids);
        return;
      }
      const passo = e.shiftKey ? 10 : 1;
      const deslocar = (dx, dy) => {
        e.preventDefault();
        patchElementos(selecao.pageIndex, ids, el => ({ x: el.x + dx, y: el.y + dy }));
      };
      if (e.key === 'ArrowUp') deslocar(0, -passo);
      if (e.key === 'ArrowDown') deslocar(0, passo);
      if (e.key === 'ArrowLeft') deslocar(-passo, 0);
      if (e.key === 'ArrowRight') deslocar(passo, 0);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecao, selectedIds, elementosSelecionados, canvas, undo]);

  // Alvos do Moveable = nós DOM dos elementos selecionados (nunca durante edição de
  // texto nem para camadas trancadas — trancada não ganha alças). Com mais de um
  // alvo o Moveable entra em modo grupo: caixa única, só arrasto.
  const moveableTargets = useMemo(() => {
    if (!selecao || editingId) return [];
    return elementosSelecionados
      .filter(e => !e.bloqueado)
      .map(e => elementRefs.current[e.el_id])
      .filter(Boolean);
    // elementRefs é um ref mutável: recalcula quando muda a seleção/canvas/página
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecao, editingId, elementosSelecionados, canvas]);

  const moveableTarget = moveableTargets.length > 1 ? moveableTargets : (moveableTargets[0] || null);

  // Guias de snap: bordas/centro da página + demais elementos da mesma página
  const elementGuidelines = useMemo(() => {
    if (!selecao) return [];
    return (canvas.paginas[selecao.pageIndex]?.elementos || [])
      .filter(e => !selectedIds.includes(e.el_id))
      .map(e => elementRefs.current[e.el_id])
      .filter(Boolean);
  }, [selecao, selectedIds, canvas]);

  const temItens = canvas.paginas.some(p => p.elementos.some(e => e.tipo === 'itens'));

  // O box de seleção do Moveable é calculado a partir do DOM no momento em que o
  // `target` muda — ele não se realinha sozinho quando o zoom muda ou a área rola,
  // pois a posição/tamanho na tela do elemento muda sem o target (ref) trocar.
  // Sem isso, o box fica "grudado" na posição antiga, desalinhado do conteúdo real.
  useEffect(() => {
    moveableRef.current?.updateRect();
  }, [zoom, selecao?.pageIndex, selectedIds]);

  return (
    <div className="flex-1 flex overflow-hidden min-h-0">
      {/* Paleta de elementos (escondida no modo conversa com o agente) */}
      {!ocultarPaleta && (
        <aside className={D.paletteAside}>
          <ElementsSidebar
            onAddElemento={addElemento}
            onAddVariavel={(chave) => addElemento('variavel', { conteudo: { chave } })}
            temItens={temItens}
            paginas={canvas.paginas}
            paginaAtiva={paginaAtiva}
            onSelectPagina={(i) => {
              setPaginaAtiva(i);
              pageRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            onAddPagina={addPagina}
            onRemovePagina={removePagina}
          />
        </aside>
      )}

      {/* Área de trabalho */}
      <div className="flex-1 min-w-0 relative flex flex-col overflow-hidden">
        {/* Toolbar do elemento selecionado */}
        {elementoSelecionado && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40">
            <ElementToolbar
              elemento={elementoSelecionado}
              editing={editingId === selected?.elId}
              onChangeEstilo={(patch) => patchElemento(selected, { estilo: { ...elementoSelecionado.estilo, ...patch } })}
              onChangeConteudo={(patch) => patchElemento(selected, { conteudo: { ...elementoSelecionado.conteudo, ...patch } })}
              onDuplicar={() => duplicarElemento(selected)}
              onRemover={() => removeElemento(selected)}
              onMoverZ={(dir) => moverZ(selected, dir)}
              onEditarItens={elementoSelecionado.tipo === 'itens' ? () => setItensDialogOpen(true) : undefined}
              validadeDias={proposta.validade_dias}
              onChangeValidadeDias={(dias) => onChange({ validade_dias: dias })}
              onEditarContato={elementoSelecionado.tipo === 'dados_cliente' ? () => setContatoDialogOpen(true) : undefined}
              onToggleBloqueio={() => toggleBloqueio([selected.elId], undefined, selected.pageIndex)}
            />
          </div>
        )}

        {/* Toolbar da seleção múltipla (agrupar/desagrupar/duplicar/trancar/excluir) */}
        {multiSelecao && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40">
            <SelecaoToolbar
              quantidade={selectedIds.length}
              temGrupo={gruposSelecionados.length > 0}
              todosTrancados={elementosSelecionados.every(e => e.bloqueado)}
              onAgrupar={() => agrupar()}
              onDesagrupar={() => desagrupar()}
              onDuplicar={() => duplicarElementos(selecao.pageIndex, selectedIds)}
              onRemover={() => removerElementos(selecao.pageIndex, selectedIds)}
              onMoverZ={(dir) => moverZ(null, dir)}
              onToggleBloqueio={() => toggleBloqueio(selectedIds, !elementosSelecionados.every(e => e.bloqueado), selecao.pageIndex)}
            />
          </div>
        )}

        <div
          ref={scrollRef}
          className="flex-1 overflow-auto py-10 px-6"
          onMouseDown={(e) => { if (e.button === 2) return; limparSelecao(); setEditingId(null); }}
          onScroll={() => moveableRef.current?.updateRect()}
        >
          <div style={{ width: PAGE_W * zoom }} className="mx-auto">
            <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', width: PAGE_W }} className="space-y-8">
              {canvas.paginas.map((pagina, pageIndex) => (
                <ContextMenu key={pageIndex}>
                <div className="relative">
                  <div className={D.pageLabel}>
                    Página {pageIndex + 1}
                  </div>
                  <ContextMenuTrigger asChild>
                  <div
                    ref={node => { pageRefs.current[pageIndex] = node; }}
                    // Sem overflow-hidden aqui: o Moveable é renderizado DENTRO desta div
                    // (mesmo ancestral position:relative usado como `container`), e suas alças/
                    // linha de rotação frequentemente ficam fora dos limites da página (ex.: acima
                    // da borda superior). O clipping do CONTEÚDO da página fica no wrapper interno.
                    className={`relative shadow-lg rounded-sm ${paginaAtiva === pageIndex ? 'ring-1 ring-[#7C3AED]/30' : ''}`}
                    style={{ width: PAGE_W, height: PAGE_H, background: pagina.fundo_cor || '#ffffff' }}
                    onMouseDown={(e) => {
                      if (e.button === 2) return; // clique direito: seleção é resolvida no onContextMenu
                      e.stopPropagation();
                      setPaginaAtiva(pageIndex);
                      limparSelecao();
                      setEditingId(null);
                    }}
                    // Clique direito: seleciona o elemento sob o cursor (hit-test — o evento
                    // chega aqui na página, não no elemento) e deixa o Radix abrir o menu.
                    // Se o elemento já faz parte da seleção atual, a seleção é preservada
                    // (senão o menu de uma seleção múltipla viraria menu de um elemento só).
                    onContextMenu={(e) => {
                      e.stopPropagation();
                      const hit = hitTestElemento(e, pageIndex);
                      setPaginaAtiva(pageIndex);
                      setEditingId(null);
                      if (!hit) { limparSelecao(); return; }
                      if (selecao?.pageIndex === pageIndex && selectedIds.includes(hit.el_id)) return;
                      selecionarElemento(pageIndex, hit.el_id);
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDropElemento(e, pageIndex)}
                  >
                    <div className="absolute inset-0 overflow-hidden rounded-sm">
                      {/* Página em branco (ex.: "Criar com IA"): diz por onde começar em vez
                          de mostrar um retângulo vazio. pointer-events-none deixa o drop passar. */}
                      {pagina.elementos.length === 0 && (
                        <div className={D.pageEmpty}>
                          <p className={D.pageEmptyText}>
                            Página em branco. Arraste elementos da paleta, ou peça ao agente para montar o documento.
                          </p>
                        </div>
                      )}
                      {pagina.elementos.map(elemento => (
                        <CanvasElement
                          key={elemento.el_id}
                          elemento={elemento}
                          proposta={proposta}
                          selected={selecao?.pageIndex === pageIndex && selectedIds.includes(elemento.el_id)}
                          editing={editingId === elemento.el_id}
                          registerRef={registerRef}
                          // Shift/Ctrl+clique soma à seleção; clique simples num membro
                          // de grupo seleciona o grupo inteiro.
                          onSelect={(e) => {
                            setPaginaAtiva(pageIndex);
                            const modificador = e?.shiftKey || e?.ctrlKey || e?.metaKey;
                            // Clicar num elemento que já faz parte de uma seleção múltipla
                            // mantém a seleção (senão o clique para arrastar o bloco
                            // reduziria tudo a um elemento só).
                            if (!modificador && multiSelecao && selecao?.pageIndex === pageIndex && selectedIds.includes(elemento.el_id)) return;
                            selecionarElemento(pageIndex, elemento.el_id, { toggle: modificador });
                            setEditingId(null);
                          }}
                          onStartEdit={() => { selecionarIds(pageIndex, [elemento.el_id]); setEditingId(elemento.el_id); }}
                          onCommitHtml={(html) => {
                            patchElemento({ pageIndex, elId: elemento.el_id }, { conteudo: { ...elemento.conteudo, html } });
                            setEditingId(null);
                          }}
                        />
                      ))}
                    </div>

                    {/* Moveable: drag / resize / rotate do elemento selecionado. Renderizado
                        DENTRO da própria página — precisa compartilhar o mesmo ancestral
                        position:relative que os elementos (CanvasElement), senão o delta que o
                        Moveable calcula relativo à página é aplicado sobre um ancestral errado
                        (ex.: a área de trabalho inteira), desalinhando a caixa do conteúdo real. */}
                    {selecao?.pageIndex === pageIndex && moveableTarget && (
                      <Moveable
                        ref={moveableRef}
                        target={moveableTarget}
                        container={pageRefs.current[pageIndex]}
                        draggable
                        // Seleção múltipla/grupo: só arrasto em bloco (redimensionar e
                        // girar vários de uma vez distorceria cada elemento)
                        resizable={!multiSelecao}
                        rotatable={!multiSelecao}
                        snappable
                        snapThreshold={5}
                        elementGuidelines={elementGuidelines}
                        verticalGuidelines={[0, PAGE_W / 2, PAGE_W]}
                        horizontalGuidelines={[0, PAGE_H / 2, PAGE_H]}
                        elementSnapDirections={{ top: true, left: true, bottom: true, right: true, center: true, middle: true }}
                        snapDirections={{ top: true, left: true, bottom: true, right: true, center: true, middle: true }}
                        throttleDrag={0}
                        throttleResize={0}
                        throttleRotate={0}
                        keepRatio={elementoSelecionado?.tipo === 'imagem'}
                        origin={false}
                        rotationPosition="top"
                        renderDirections={['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se']}
                        onDragStart={() => {
                          dragOrigin.current = { x: elementoSelecionado.x, y: elementoSelecionado.y };
                        }}
                        onDrag={({ target, dist }) => {
                          // Moveable calcula `left`/`top` em pixels de tela "crus" (1 px de mouse =
                          // 1 px de delta), sem compensar o transform:scale(zoom) do ancestral da
                          // página — por isso não usamos esses valores direto, e sim `dist`
                          // (distância acumulada desde o início do drag) dividido pelo zoom.
                          const x = dragOrigin.current.x + dist[0] / zoom;
                          const y = dragOrigin.current.y + dist[1] / zoom;
                          target.style.left = `${x}px`;
                          target.style.top = `${y}px`;
                          dragState.current = { x, y };
                        }}
                        onDragEnd={() => {
                          if (dragState.current) {
                            patchElemento(selected, { x: Math.round(dragState.current.x), y: Math.round(dragState.current.y) });
                            dragState.current = null;
                          }
                          dragOrigin.current = null;
                        }}
                        onResizeStart={() => {
                          dragOrigin.current = {
                            x: elementoSelecionado.x,
                            y: elementoSelecionado.y,
                            largura: elementoSelecionado.largura,
                            altura: elementoSelecionado.altura,
                          };
                        }}
                        onResize={({ target, dist, direction }) => {
                          // Mesma compensação do onDrag: dist[0]/dist[1] são a variação crua de
                          // largura/altura desde o início do resize, sem dividir por zoom.
                          const largura = Math.max(20, dragOrigin.current.largura + dist[0] / zoom);
                          const altura = Math.max(20, dragOrigin.current.altura + dist[1] / zoom);
                          // Ao redimensionar pelas alças esquerda/topo, a origem (x/y) também se
                          // desloca — desloca na mesma proporção que a largura/altura cresceram.
                          const x = direction[0] === -1 ? dragOrigin.current.x + (dragOrigin.current.largura - largura) : dragOrigin.current.x;
                          const y = direction[1] === -1 ? dragOrigin.current.y + (dragOrigin.current.altura - altura) : dragOrigin.current.y;
                          target.style.width = `${largura}px`;
                          target.style.height = `${altura}px`;
                          target.style.left = `${x}px`;
                          target.style.top = `${y}px`;
                          dragState.current = { largura, altura, x, y };
                        }}
                        onResizeEnd={() => {
                          if (dragState.current) {
                            patchElemento(selected, {
                              largura: Math.round(dragState.current.largura),
                              altura: Math.round(dragState.current.altura),
                              x: Math.round(dragState.current.x),
                              y: Math.round(dragState.current.y),
                            });
                            dragState.current = null;
                          }
                          dragOrigin.current = null;
                        }}
                        onRotate={({ target, rotation }) => {
                          target.style.transform = `rotate(${rotation}deg)`;
                          dragState.current = { rotacao: rotation };
                        }}
                        onRotateEnd={() => {
                          if (dragState.current) {
                            patchElemento(selected, { rotacao: Math.round(dragState.current.rotacao) });
                            dragState.current = null;
                          }
                        }}
                        // ── Arrasto em bloco (seleção múltipla / grupo) ──
                        // Mesma compensação de zoom do arrasto simples, aplicada a cada
                        // alvo a partir da posição que ele tinha quando o drag começou.
                        onDragGroupStart={() => {
                          dragOrigin.current = Object.fromEntries(
                            elementosSelecionados.map(el => [el.el_id, { x: el.x, y: el.y }]),
                          );
                          dragState.current = null;
                        }}
                        onDragGroup={({ events, dist }) => {
                          const [dx, dy] = dist || events[0]?.dist || [0, 0];
                          const posicoes = {};
                          events.forEach(ev => {
                            const elId = ev.target.dataset.elid;
                            const origem = dragOrigin.current?.[elId];
                            if (!origem) return;
                            const x = origem.x + dx / zoom;
                            const y = origem.y + dy / zoom;
                            ev.target.style.left = `${x}px`;
                            ev.target.style.top = `${y}px`;
                            posicoes[elId] = { x, y };
                          });
                          dragState.current = posicoes;
                        }}
                        onDragGroupEnd={() => {
                          const posicoes = dragState.current;
                          if (posicoes && Object.keys(posicoes).length) {
                            patchElementos(selecao.pageIndex, Object.keys(posicoes), el => ({
                              x: Math.round(posicoes[el.el_id].x),
                              y: Math.round(posicoes[el.el_id].y),
                            }));
                          }
                          dragState.current = null;
                          dragOrigin.current = null;
                        }}
                      />
                    )}
                  </div>
                  </ContextMenuTrigger>
                </div>

                {/* Menu de contexto (clique direito): ações do elemento sob o cursor,
                    ou da página quando o clique foi numa área vazia */}
                <ContextMenuContent className="w-56">
                  {selecao?.pageIndex === pageIndex && multiSelecao ? (
                    <>
                      <ContextMenuItem onSelect={() => agrupar()}>
                        <Iconify icon="mdi:group" width={14} className="mr-2" /> Agrupar ({selectedIds.length}) — Ctrl+G
                      </ContextMenuItem>
                      {gruposSelecionados.length > 0 && (
                        <ContextMenuItem onSelect={() => desagrupar()}>
                          <Iconify icon="mdi:ungroup" width={14} className="mr-2" /> Desagrupar
                        </ContextMenuItem>
                      )}
                      <ContextMenuItem onSelect={() => duplicarElementos(selecao.pageIndex, selectedIds)}>
                        <Iconify icon="mdi:content-copy" width={14} className="mr-2" /> Duplicar seleção
                      </ContextMenuItem>
                      <ContextMenuSeparator />
                      <ContextMenuItem onSelect={() => moverZ(null, 1)}>
                        <Iconify icon="mdi:arrange-bring-to-front" width={14} className="mr-2" /> Trazer para frente
                      </ContextMenuItem>
                      <ContextMenuItem onSelect={() => moverZ(null, -1)}>
                        <Iconify icon="mdi:arrange-send-to-back" width={14} className="mr-2" /> Enviar para trás
                      </ContextMenuItem>
                      <ContextMenuSeparator />
                      <ContextMenuItem
                        className="text-red-600 focus:text-red-600"
                        onSelect={() => removerElementos(selecao.pageIndex, selectedIds)}
                      >
                        <Iconify icon="mdi:trash-can-outline" width={14} className="mr-2" /> Excluir seleção
                      </ContextMenuItem>
                    </>
                  ) : selected?.pageIndex === pageIndex && elementoSelecionado ? (
                    <>
                      {elementoSelecionado.tipo === 'texto' && (
                        <ContextMenuItem onSelect={() => setEditingId(elementoSelecionado.el_id)}>
                          <Iconify icon="eva:edit-2-outline" width={14} className="mr-2" /> Editar texto
                        </ContextMenuItem>
                      )}
                      {elementoSelecionado.tipo === 'itens' && (
                        <ContextMenuItem onSelect={() => setItensDialogOpen(true)}>
                          <Iconify icon="mdi:table-edit" width={14} className="mr-2" /> Editar itens do orçamento
                        </ContextMenuItem>
                      )}
                      {elementoSelecionado.tipo === 'dados_cliente' && (
                        <ContextMenuItem onSelect={() => setContatoDialogOpen(true)}>
                          <Iconify icon="mdi:account-edit-outline" width={14} className="mr-2" /> Editar dados do cliente
                        </ContextMenuItem>
                      )}
                      <ContextMenuItem onSelect={() => duplicarElemento(selected)}>
                        <Iconify icon="mdi:content-copy" width={14} className="mr-2" /> Duplicar
                      </ContextMenuItem>
                      <ContextMenuItem onSelect={() => toggleBloqueio([elementoSelecionado.el_id], undefined, pageIndex)}>
                        <Iconify icon="mdi:lock-outline" width={14} className="mr-2" /> Trancar camada
                      </ContextMenuItem>
                      {elementoSelecionado.grupo_id && (
                        <ContextMenuItem onSelect={() => desagrupar([elementoSelecionado.grupo_id], pageIndex)}>
                          <Iconify icon="mdi:ungroup" width={14} className="mr-2" /> Desagrupar
                        </ContextMenuItem>
                      )}
                      <ContextMenuSeparator />
                      <ContextMenuItem onSelect={() => moverZ(selected, 1)}>
                        <Iconify icon="mdi:arrange-bring-to-front" width={14} className="mr-2" /> Trazer para frente
                      </ContextMenuItem>
                      <ContextMenuItem onSelect={() => moverCamadaStep(elementoSelecionado.el_id, 1)}>
                        <Iconify icon="mdi:arrange-bring-forward" width={14} className="mr-2" /> Avançar uma camada
                      </ContextMenuItem>
                      <ContextMenuItem onSelect={() => moverCamadaStep(elementoSelecionado.el_id, -1)}>
                        <Iconify icon="mdi:arrange-send-backward" width={14} className="mr-2" /> Recuar uma camada
                      </ContextMenuItem>
                      <ContextMenuItem onSelect={() => moverZ(selected, -1)}>
                        <Iconify icon="mdi:arrange-send-to-back" width={14} className="mr-2" /> Enviar para trás
                      </ContextMenuItem>
                      <ContextMenuSeparator />
                      <ContextMenuItem
                        className="text-red-600 focus:text-red-600"
                        onSelect={() => removeElemento(selected)}
                      >
                        <Iconify icon="mdi:trash-can-outline" width={14} className="mr-2" /> Excluir
                      </ContextMenuItem>
                    </>
                  ) : (
                    <>
                      <ContextMenuItem onSelect={addPagina}>
                        <Iconify icon="eva:plus-fill" width={14} className="mr-2" /> Nova página
                      </ContextMenuItem>
                      {canvas.paginas.length > 1 && (
                        <ContextMenuItem
                          className="text-red-600 focus:text-red-600"
                          onSelect={() => removePagina(pageIndex)}
                        >
                          <Iconify icon="mdi:trash-can-outline" width={14} className="mr-2" /> Remover esta página
                        </ContextMenuItem>
                      )}
                    </>
                  )}
                </ContextMenuContent>
                </ContextMenu>
              ))}
            </div>
          </div>
        </div>

        {/* Controle de zoom */}
        <div className="absolute bottom-4 right-4 z-40 flex items-center gap-1 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] rounded-xl shadow-md px-1.5 py-1">
          <button
            type="button"
            onClick={() => setZoom(z => ZOOMS[Math.max(0, ZOOMS.indexOf(z) - 1)])}
            disabled={zoom === ZOOMS[0]}
            className="w-7 h-7 flex items-center justify-center rounded-md text-gray-500 dark:text-[#ABABAD] hover:bg-gray-100 dark:hover:bg-[#383838] transition-colors disabled:opacity-30"
          >
            <Iconify icon="mdi:minus" width={15} />
          </button>
          <span className="text-xs font-medium text-gray-600 dark:text-[#D1D2D3] w-11 text-center">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setZoom(z => ZOOMS[Math.min(ZOOMS.length - 1, ZOOMS.indexOf(z) + 1)])}
            disabled={zoom === ZOOMS[ZOOMS.length - 1]}
            className="w-7 h-7 flex items-center justify-center rounded-md text-gray-500 dark:text-[#ABABAD] hover:bg-gray-100 dark:hover:bg-[#383838] transition-colors disabled:opacity-30"
          >
            <Iconify icon="mdi:plus" width={15} />
          </button>
        </div>
      </div>

      {/* Sidebar direita: camadas da página ativa (estilo Canva). Escondida junto
          com a paleta no modo conversa com o agente, para o documento respirar. */}
      {!ocultarPaleta && (
        <LayersSidebar
          paginaAtiva={paginaAtiva}
          elementos={canvas.paginas[paginaAtiva]?.elementos || []}
          grupos={canvas.paginas[paginaAtiva]?.grupos || []}
          selectedIds={selecao?.pageIndex === paginaAtiva ? selectedIds : []}
          onSelectElemento={(elId, opts) => selecionarElemento(paginaAtiva, elId, opts)}
          onSelectGrupo={(grupoId, { toggle } = {}) => {
            const ids = (canvas.paginas[paginaAtiva]?.elementos || []).filter(e => e.grupo_id === grupoId).map(e => e.el_id);
            if (!ids.length) return;
            if (toggle && selecao?.pageIndex === paginaAtiva) {
              const atuais = new Set(selectedIds);
              const jaTodos = ids.every(id => atuais.has(id));
              ids.forEach(id => (jaTodos ? atuais.delete(id) : atuais.add(id)));
              selecionarIds(paginaAtiva, [...atuais]);
              return;
            }
            selecionarIds(paginaAtiva, ids);
          }}
          onMoverCamada={(elId, dir) => moverCamadaStep(elId, dir, paginaAtiva)}
          onReordenar={(ids) => reordenarCamadas(ids, paginaAtiva)}
          onRemoverElemento={(ids) => removerElementos(paginaAtiva, ids)}
          onToggleBloqueio={(ids, valor) => toggleBloqueio(ids, valor, paginaAtiva)}
          onAgrupar={(ids) => agrupar(ids, paginaAtiva)}
          onDesagrupar={(gids) => desagrupar(gids, paginaAtiva)}
          onRenomearGrupo={(gid, nome) => renomearGrupo(gid, nome, paginaAtiva)}
        />
      )}

      {/* Dialog de edição dos itens do orçamento */}
      {itensDialogOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-200" onClick={() => setItensDialogOpen(false)} />
          <div className="relative bg-white dark:bg-[#222529] rounded-2xl shadow-xl w-[min(92vw,860px)] max-h-[85vh] overflow-y-auto p-5 animate-in fade-in slide-in-from-bottom-4 duration-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center">
                  <Iconify icon="mdi:table-large" width={18} className="text-[#7C3AED]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 dark:text-[#D1D2D3]">Itens do orçamento</p>
                  <p className="text-[11px] text-gray-400">Os valores refletem na tabela do documento</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setItensDialogOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-[#D1D2D3] hover:bg-gray-100 dark:hover:bg-[#383838] transition-colors"
              >
                <Iconify icon="eva:close-outline" width={18} />
              </button>
            </div>
            <ItensTableEditavel
              itens={proposta.itens || []}
              descontoGlobalPercent={proposta.desconto_global_percent}
              onChange={onChange}
              readOnly={false}
            />
          </div>
        </div>
      )}

      {/* Dialog de edição dos dados do cliente */}
      {contatoDialogOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-200" onClick={() => setContatoDialogOpen(false)} />
          <div className="relative bg-white dark:bg-[#222529] rounded-2xl shadow-xl w-[min(92vw,420px)] p-5 animate-in fade-in slide-in-from-bottom-4 duration-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center">
                  <Iconify icon="mdi:account-outline" width={18} className="text-[#7C3AED]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 dark:text-[#D1D2D3]">Dados do cliente</p>
                  <p className="text-[11px] text-gray-400">Aparece em todos os blocos "Dados do cliente"</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setContatoDialogOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-[#D1D2D3] hover:bg-gray-100 dark:hover:bg-[#383838] transition-colors"
              >
                <Iconify icon="eva:close-outline" width={18} />
              </button>
            </div>
            <div className="space-y-2.5">
              <Input
                value={proposta.contato?.nome || ''}
                onChange={e => onChange({ contato: { ...proposta.contato, nome: e.target.value } })}
                placeholder="Nome do cliente"
                className="h-9 text-sm rounded-lg"
              />
              <Input
                value={proposta.contato?.email || ''}
                onChange={e => onChange({ contato: { ...proposta.contato, email: e.target.value } })}
                placeholder="E-mail"
                className="h-9 text-sm rounded-lg"
              />
              <Input
                value={proposta.contato?.telefone || ''}
                onChange={e => onChange({ contato: { ...proposta.contato, telefone: e.target.value } })}
                placeholder="Telefone"
                className="h-9 text-sm rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
